<script setup lang="ts">
    import { computed, ref } from 'vue';
    import { toFixedPrice } from '@/composables/commonFunctions'
    import { useRiskStore } from '@/stores/riskData'
    import { resolveQuoteAddonFallbackImage, resolveQuoteAddonImage } from '../config/productRegistry'
    import ShowMore from '@/components/Common/ShowMore.vue'
    const props = defineProps<{
        addon: any,
        action: string,
        index: number,
        productOutput: any
    }>();
    const emit = defineEmits<{ (e: 'select', id: any, addonType: 'grouped' | 'single'): void }>()
    const selectedAddon = (id: string) => {
        emit('select', id, 'single');
    };

    const store = useRiskStore()

    // Prefer the addon's own ProductId, falling back to the store's product id.
    const addonProductId = computed(() => props.addon?.ProductId ?? store.productId)
    const addonImageContext = computed(() => ({ productId: addonProductId.value, variant: 'single' as const }))

    // Set when the configured image fails to load, so the computed src switches to the
    // product's fallback. Reactive (rather than mutating img.src imperatively) so Vue's
    // :src binding stays authoritative and there's no render → re-error loop.
    const addonImageFailed = ref(false)

    const addonImage = computed(() => {
        const configured = resolveQuoteAddonImage(addonProductId.value, props.addon, addonImageContext.value)
        if (!configured) return null

        if (!addonImageFailed.value) return configured

        return resolveQuoteAddonFallbackImage(addonProductId.value, props.addon, addonImageContext.value) ?? configured
    })

    // The browser "checks" existence by attempting to load the image; on failure we
    // switch to the fallback on the next render.
    const onAddonImageError = () => {
        addonImageFailed.value = true
    }

    // Documents for this addon come from ProductOutput.AddOnDocuments, keyed by addon Id.
    // Each entry's Value is a list of { Key: document name, Value: document link }.
    const addonDocuments = computed(() =>
        props.productOutput?.AddOnDocuments
            ?.find((entry: any) => entry.Key === props.addon.Id)
            ?.Value ?? []
    )
</script>

<template>
    <div class="card-shadow card rounded-lg mb-4 overflow-hidden">
        <div class="card-body p-0">
            <div class="row no-gutters border-bottom">
                <!-- Left: addon image + name + code -->
                <div class="col-12 col-xl-2 border-right d-flex justify-content-center">
                    <div class="w-100 text-center px-3 py-4">
                        <div v-if="addonImage"
                             class="d-flex justify-content-center mx-auto mb-3"
                             style="width: 80px; height: 80px;">
                            <img loading="lazy"
                                 class="img-fluid"
                                 :src="addonImage"
                                 :alt="addon.CustomerFacingName"
                                 @error="onAddonImageError" />
                        </div>

                        <div class="font-weight-bold text-uppercase text-dark small">
                            {{ addon.Name }}
                        </div>
                    </div>
                </div>

                <!-- Middle: cover details + expandable description/documents -->
                <div class="col-12 col-xl-8">
                    <div class="row p-15 h-100">
                        <div class="col-12 d-flex pl-lg-2">

                            <div class="border p-15" :class="addonDocuments.length ? 'rounded-left w-50' : 'rounded w-100'">
                                <div class="small text-uppercase text-muted font-weight-bold mb-3">
                                    <em class="far fa-info-circle mr-2 text-info"></em>
                                    Cover Details
                                </div>

                                <div class="pb-1">
                                    <ShowMore v-if="addon.Description" :lines="6">
                                        <span class="text-muted fs-12" v-html="addon.Description"></span>
                                    </ShowMore>
                                    <span v-else class="text-muted fs-12">No cover details available.</span>
                                </div>
                            </div>

                            <!-- Documents -->
                            <div v-if="addonDocuments.length" class="border rounded-right w-50 p-15">
                                <div class="small text-uppercase text-muted font-weight-bold mb-3">
                                    <em class="far fa-download mr-2 text-info"></em>
                                    Documents
                                </div>

                                <div class="d-flex flex-column">
                                    <a v-for="(doc, docIndex) in addonDocuments"
                                       :key="docIndex"
                                       :href="doc.Value"
                                       target="_blank"
                                       rel="noopener"
                                       class="text-muted fs-12 mb-1">
                                        <em class="far fa-file-alt mr-2"></em>
                                        {{ doc.Key }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Right: price + selection -->
                <div class="col-12 col-xl-2 border-left d-flex justify-content-center">
                    <div class="w-100 text-right px-3 py-4">
                        <div>
                            <span class="h4 text-muted mr-1">£</span>
                            <span class="display-4 font-weight-bold text-dark" style="font-size: 2.3rem;">
                                {{ toFixedPrice(addon.OriginalTotalPayable).replace('£', '') }}
                            </span>
                        </div>

                        <div class="small text-muted mb-3">per year</div>

                        <input type="checkbox"
                               class="d-none"
                               :checked="addon.IsSelected"
                               @change="selectedAddon(addon.Code)"
                               :name="`AddonSelection[${index}]`"
                               :id="`${addon.Code}_AddonSelection_${index}`"
                               :disabled="action.toLowerCase().includes('mta')" />

                        <button v-if="!addon.IsSelected"
                                type="button"
                                class="btn btn-primary btn-block font-weight-bold py-2 mb-2"
                                :disabled="action.toLowerCase().includes('mta')"
                                @click="selectedAddon(addon.Code)">
                            <em class="far fa-check-circle mr-2"></em>
                            Add to Cover
                        </button>
                        <button v-else
                                type="button"
                                class="btn btn-danger btn-block font-weight-bold py-2 mb-2"
                                :disabled="action.toLowerCase().includes('mta')"
                                @click="selectedAddon(addon.Code)">
                            <em class="far fa-times-circle mr-2"></em>
                            Remove
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
